import type { OnboardingWizardStepKey } from '@solar/contracts';

import { Button } from '../../components/Button';
import { cx } from '../../lib/class-names';

import { ONBOARDING_STEP_LABELS, ONBOARDING_STEP_ORDER, stepIndex } from './onboarding-content';
import styles from './OnboardingWizard.module.css';

interface OnboardingWizardShellProps {
  readonly activeStep: OnboardingWizardStepKey | 'complete';
  readonly completedSteps: ReadonlySet<OnboardingWizardStepKey>;
  readonly title: string;
  readonly description: string;
  readonly children: React.ReactNode;
  readonly backLabel?: string | null;
  readonly onBack?: (() => void) | null;
  readonly continueLabel: string;
  readonly onContinue: () => void;
  readonly continueDisabled?: boolean;
  readonly continuePending?: boolean;
  readonly hideActions?: boolean;
}

export function OnboardingWizardShell({
  activeStep,
  completedSteps,
  title,
  description,
  children,
  backLabel = 'Back',
  onBack = null,
  continueLabel,
  onContinue,
  continueDisabled = false,
  continuePending = false,
  hideActions = false,
}: OnboardingWizardShellProps): React.JSX.Element {
  const stepNumber =
    activeStep === 'complete' ? ONBOARDING_STEP_ORDER.length : stepIndex(activeStep) + 1;

  return (
    <div className={styles.wizard}>
      <div className={styles.inner}>
        <header className={styles.brandRow}>
          <span className={styles.brandWordmark}>SEAMONTECH</span>
          <h1 className={styles.heading}>Welcome / Account setup</h1>
          <p className={styles.subheading}>
            Complete these steps so we can prepare your solar repayment account.
          </p>
        </header>

        {activeStep !== 'complete' && (
          <>
            <p className={styles.progressMeta}>
              Step {stepNumber} of {ONBOARDING_STEP_ORDER.length}
            </p>

            <div className={styles.stepper} aria-label="Onboarding progress">
              {ONBOARDING_STEP_ORDER.map((step) => {
                const isComplete = completedSteps.has(step);
                const isCurrent = activeStep === step;

                return (
                  <div
                    key={step}
                    className={cx(
                      styles.stepChip,
                      isComplete && styles.stepChipComplete,
                      isCurrent && styles.stepChipCurrent,
                    )}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    {ONBOARDING_STEP_LABELS[step]}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <section className={styles.card} aria-labelledby="onboarding-step-title">
          <h2 className={styles.stepTitle} id="onboarding-step-title">
            {title}
          </h2>
          <p className={styles.stepDescription}>{description}</p>

          {children}

          {!hideActions && (
            <div className={styles.actions}>
              {onBack !== null && backLabel !== null ? (
                <button type="button" className={styles.backButton} onClick={onBack}>
                  ← {backLabel}
                </button>
              ) : (
                <span className={styles.backSpacer} aria-hidden="true">
                  Back
                </span>
              )}

              <Button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled || continuePending}
              >
                {continuePending ? 'Saving…' : continueLabel}
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
